import {  Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment';
import { SiteService } from '../site/site.service';
import { Store } from '@ngrx/store';
import * as fromUser from '../user/store/reducers';
import { take, mergeMap } from 'rxjs/operators';
import { timer, of, combineLatest } from 'rxjs';
import { LocalStorageService } from '../local-storage/local-storage.service';
import { MsalService } from '../msal';
import { AppRouteService } from '../app-route.service';
import { UserStoreService } from '../user/store/user-store.service';

(window as any).global = window;

@Injectable()
export class AuthService {
	user$ = this.userStoreService.user$;
	refreshSubscription: any;
	userError$ = this.userStoreService.userError$;

	constructor(
		public router: Router,
		public siteService: SiteService,
		private store: Store<fromUser.State>,
		private userStoreService: UserStoreService,
		private localStorageService: LocalStorageService,
		private msalService: MsalService,
		private appRouteService: AppRouteService
	) {}

	/**
	 * Handles the login be calling authorize on auth0
	 * @param signInType number 0 or 1 to represent emcloud AD login vs ping login
	 */
	public login(signInType): void {
		if (signInType === 2) {
			this.msalService.loginRedirect({
				scopes: environment.msal.b2cScopes,
			});
		} else {
			this.msalService.loginRedirect({
				scopes: environment.msal.b2cScopes,
				domainHint: signInType === 0 ? environment.msal.exxonSSODomainHint : environment.msal.pingDomainHint,
			});
		}
	}

	public async handleAuthentication(account = null): Promise<void> {
		if (!account) {
			const allAccounts = this.msalService.getAllAccounts();
			if (allAccounts.length === 0 && window.location.pathname === '/callback') {
				return;
			}

			if (allAccounts.length >= 1) {
				account = this.msalService.getAccountByUsername(this.localStorageService.getUserEmail());
			} else {
				this.clearAndReset();
				return;
			}
		}

		if (account) {
			this.msalService.acquireTokenSilent({ scopes: environment.msal.apiScopes, account: account }).subscribe(
				token => {
					if (token && token.accessToken && token.idToken) {
						this.setSession(token);
						this.scheduleRenewal(token.account);
						this.loadUser();
					}
				},
				error => {
					this.clearAndReset();
				}
			);
		} else {
			this.clearAndReset();
		}
	}

	clearAndReset() {
		this.clearLocalStorage();
		this.router.navigate(['']);
	}

	/**
	 * Dispatches the GetMe action and creates an observable.
	 * If there is an error getting the user, the page is directed to the 'forbidden' screen
	 * If we get the user, navigate to default component
	 */
	private loadUser(): void {
		this.userStoreService.dispatchGetMe();
		combineLatest([this.user$, this.userError$])
			.pipe(take(2))
			.subscribe(([user, error]) => {
				if (error) {
					this.clearLocalStorage();
					this.router.navigate(['/forbidden']);
				} else if (user) {
					const previousUrl = sessionStorage.getItem('previous_url');
					if (previousUrl && previousUrl !== '/callback' && previousUrl !== '/login' && previousUrl !== '/') {
						this.router.navigate([previousUrl]);
					} else {
						this.router.navigate([this.appRouteService.getListRoute(user.profile.defaultSiteId)]);
					}

					sessionStorage.removeItem('previous_url');
				}
			});
	}

	/**
	 * Sets the session data returned from auth0 and then schedules a timer
	 * for renewing the token
	 * @param authResult
	 */
	private setSession(authResult): void {
		// Set the time that the Access Token will expire at
		const expiresAt = authResult.expiresOn.getTime();
		this.localStorageService.setAccessToken(authResult.accessToken);
		this.localStorageService.setIdToken(authResult.idToken);
		this.localStorageService.setExpiresAt(expiresAt);
		this.localStorageService.setUserEmail(authResult.account.username);
	}

	/**
	 * Clears local storage
	 */
	clearLocalStorage() {
		this.localStorageService.clearStorage();
	}

	/**
	 * Handles the logout by clearing local storage and then logging out of
	 * the federated service
	 */
	public logout(): void {
		this.msalService.logout({ postLogoutRedirectUri: environment.msal.logoutUri });
		this.clearLocalStorage();
	}

	/**
	 * Check whether the current time is past the
	 * Access Token's expiry time
	 */
	public isAuthenticated(): boolean {
		const expiresAt = this.localStorageService.getExpirestAt() || 0;
		return new Date().getTime() < expiresAt;
	}

	/**
	 * This function handles renewing an access token. If the user has not been active in the app
	 * for a given period of time the user will be logged out. Otherwise the users sessions will
	 * be checked and a new access token will be retrieved
	 */
	public renewTokens(account): void {
		if (this.localStorageService.getLastActivity() && parseFloat(this.localStorageService.getActivityTimeout()) - Date.now() < 0) {
			this.logout();
		} else {
			if (!account) return;
			this.msalService.acquireTokenSilent({ scopes: environment.msal.apiScopes, account: account, forceRefresh: true }).subscribe(
				token => {
					if (token) {
						this.setSession(token);
						this.scheduleRenewal(account);
						this.userStoreService.dispatchGetMe();
					}
				},
				error => {
					this.logout();
				}
			);
		}
	}

	/**
	 * Sets up a timer to renew the token. This timer will try to renew the token
	 * once it expires. Additionally, it will unsubscribe from any previous timers.
	 */
	public scheduleRenewal(account) {
		if (!this.isAuthenticated()) {
			return;
		}
		this.unscheduleRenewal();

		const expiresAt = this.localStorageService.getExpirestAt() * 1;

		const source = of(expiresAt).pipe(
			mergeMap(expAt => {
				const now = Date.now();
				const expirationTime = expAt - now;
				const tokenRenewalTime = expirationTime - 120000;

				return timer(tokenRenewalTime);
			})
		);

		// Once the delay time from above is
		// reached, get a new JWT and schedule
		// additional refreshes
		this.refreshSubscription = source.subscribe(() => {
			this.renewTokens(account);
		});
	}

	/**
	 * Unsubscribes from previous renewal timer
	 */
	public unscheduleRenewal() {
		if (!this.refreshSubscription) {
			return;
		}
		this.refreshSubscription.unsubscribe();
	}
}
